import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Coffee, Star, ArrowRight, ArrowLeft, Users, CupSoda, Droplets } from 'lucide-react';
import './CoffeePriceModal.css';
import './CrowdLevelModal.css';

type CoffeeKind = 'espresso' | 'long-black' | 'latte' | 'flat-white' | 'cappuccino' | 'mocha' | 'iced-latte' | 'iced-long-black' | 'macchiato' | 'piccolo' | 'filter' | 'cold-brew' | 'other';
type CrowdLevel = 'low' | 'moderate' | 'high' | 'very high';

interface CoffeePriceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { coffee: CoffeeKind; price: number; quality: number; crowd: CrowdLevel; customCoffeeName?: string }) => void;
}

const COFFEE_TYPES: Array<{ id: CoffeeKind; label: string; icon: React.ComponentType<{ size?: number }> }> = [
  { id: 'espresso', label: 'Espresso', icon: Coffee },
  { id: 'long-black', label: 'Long Black', icon: Coffee },
  { id: 'latte', label: 'Latte', icon: Coffee },
  { id: 'flat-white', label: 'Flat White', icon: Coffee },
  { id: 'cappuccino', label: 'Cappuccino', icon: Coffee },
  { id: 'mocha', label: 'Mocha', icon: Coffee },
  { id: 'iced-latte', label: 'Iced Latte', icon: CupSoda },
  { id: 'iced-long-black', label: 'Iced Long Black', icon: CupSoda },
  { id: 'macchiato', label: 'Macchiato', icon: Droplets },
  { id: 'piccolo', label: 'Piccolo', icon: Coffee },
  { id: 'filter', label: 'Filter', icon: Coffee },
  { id: 'cold-brew', label: 'Cold Brew', icon: CupSoda },
  { id: 'other', label: 'Other', icon: Coffee },
];

const CROWD_LEVELS: Array<{ id: CrowdLevel; label: string }> = [
  { id: 'low', label: 'Low' },
  { id: 'moderate', label: 'Moderate' },
  { id: 'high', label: 'High' },
  { id: 'very high', label: 'Packed' },
];

const CoffeePriceModal: React.FC<CoffeePriceModalProps> = ({ isOpen, onClose, onSubmit }) => {
  const [step, setStep] = React.useState<1 | 2 | 3>(1);
  const [coffee, setCoffee] = React.useState<CoffeeKind | null>(null);
  const [customCoffeeName, setCustomCoffeeName] = React.useState<string>('');
  const [price, setPrice] = React.useState<string>('');
  const [quality, setQuality] = React.useState<number>(0);
  const [crowd, setCrowd] = React.useState<CrowdLevel | null>(null);
  const [isFilling, setIsFilling] = React.useState(false);
  const [fillProgress, setFillProgress] = React.useState(0);
  const fillTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const progressIntervalRef = React.useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = React.useRef<number | null>(null);
  const submitDataRef = React.useRef<{ coffee: CoffeeKind | null; price: string; quality: number; crowd: CrowdLevel | null; customCoffeeName: string }>({
    coffee: null,
    price: '',
    quality: 0,
    crowd: null,
    customCoffeeName: '',
  });

  // Keep ref updated with current values
  React.useEffect(() => {
    submitDataRef.current = { coffee, price, quality, crowd, customCoffeeName };
  }, [coffee, price, quality, crowd, customCoffeeName]);

  // Lock body scroll when modal is open
  React.useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      
      return () => {
        const scrollY = document.body.style.top;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      };
    }
  }, [isOpen]);

  const canNext = step === 1 
    ? !!coffee && (coffee !== 'other' || (coffee === 'other' && customCoffeeName.trim().length > 0))
    : step === 2 
    ? !!price && Number(price) > 0 && quality > 0 
    : !!crowd;

  const submit = () => {
    if (!coffee || !crowd || !price) return;
    if (coffee === 'other' && !customCoffeeName.trim()) return;
    const parsed = Number(price);
    if (isNaN(parsed) || parsed <= 0) return;
    onSubmit({ 
      coffee, 
      price: parsed, 
      quality, 
      crowd,
      customCoffeeName: coffee === 'other' ? customCoffeeName.trim() : undefined
    });
    onClose();
    setStep(1);
    setCoffee(null);
    setCustomCoffeeName('');
    setPrice('');
    setQuality(0);
    setCrowd(null);
    setIsFilling(false);
    setFillProgress(0);
  };

  const handleSubmit = () => {
    if (!coffee || !crowd || !price) return;
    
    // If already filling, submit immediately
    if (isFilling) {
      if (fillTimeoutRef.current) clearTimeout(fillTimeoutRef.current);
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
      setIsFilling(false);
      setFillProgress(0);
      submit();
      return;
    }
  };

  // Start fill animation when crowd level is selected on step 3
  React.useEffect(() => {
    if (step === 3 && crowd && isOpen) {
      // Clear any existing timers first
      if (fillTimeoutRef.current) clearTimeout(fillTimeoutRef.current);
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
      
      // Small delay to ensure UI is ready
      const initTimer = setTimeout(() => {
        // Start fill animation
        setIsFilling(true);
        setFillProgress(0);
        startTimeRef.current = Date.now();
        
        // Update progress every 16ms (60fps)
        progressIntervalRef.current = setInterval(() => {
          if (startTimeRef.current) {
            const elapsed = Date.now() - startTimeRef.current;
            const progress = Math.min((elapsed / 2000) * 100, 100);
            setFillProgress(progress);
            
            if (progress >= 100) {
              if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
            }
          }
        }, 16);
        
        // Auto-submit after 2 seconds
        fillTimeoutRef.current = setTimeout(() => {
          if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
          setIsFilling(false);
          setFillProgress(0);
          // Use latest values from ref
          const data = submitDataRef.current;
          if (data.coffee && data.crowd && data.price) {
            if (data.coffee === 'other' && !data.customCoffeeName?.trim()) return;
            const parsed = Number(data.price);
            if (!isNaN(parsed) && parsed > 0) {
              onSubmit({ 
                coffee: data.coffee, 
                price: parsed, 
                quality: data.quality, 
                crowd: data.crowd,
                customCoffeeName: data.coffee === 'other' ? data.customCoffeeName?.trim() : undefined
              });
              onClose();
              setStep(1);
              setCoffee(null);
              setCustomCoffeeName('');
              setPrice('');
              setQuality(0);
              setCrowd(null);
            }
          }
        }, 2000);
      }, 50);
      
      return () => {
        clearTimeout(initTimer);
        if (fillTimeoutRef.current) clearTimeout(fillTimeoutRef.current);
        if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
      };
    }
  }, [crowd, step, isOpen]);

  // Reset when crowd selection is cleared or step changes
  React.useEffect(() => {
    if (!crowd || step !== 3) {
      setIsFilling(false);
      setFillProgress(0);
      if (fillTimeoutRef.current) clearTimeout(fillTimeoutRef.current);
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    }
  }, [crowd, step]);

  // Cleanup on unmount or close
  React.useEffect(() => {
    return () => {
      if (fillTimeoutRef.current) clearTimeout(fillTimeoutRef.current);
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    };
  }, []);

  // Reset when modal opens or closes
  React.useEffect(() => {
    if (isOpen) {
      setIsFilling(false);
      setFillProgress(0);
      if (fillTimeoutRef.current) clearTimeout(fillTimeoutRef.current);
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    } else {
      setStep(1);
      setCoffee(null);
      setCustomCoffeeName('');
      setPrice('');
      setQuality(0);
      setCrowd(null);
      setIsFilling(false);
      setFillProgress(0);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="recommendations-modal-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="coffee-modal"
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Fixed Header */}
          <div className="coffee-modal-header">
            <h2>{step === 1 ? 'Select coffee' : step === 2 ? 'Price & quality' : 'Crowd level'}</h2>
            <div className="coffee-header-actions">
              <button onClick={onClose} className="close-button">
                <X size={24} />
              </button>
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="coffee-modal-content">
            {step === 1 && (
              <div className="coffee-types-scroll">
                <div className="coffee-types-grid">
                  {COFFEE_TYPES.map((c) => {
                    const IconComponent = c.icon;
                    return (
                      <motion.button
                        key={c.id}
                        className={`coffee-type ${coffee === c.id ? 'selected' : ''}`}
                        whileHover={{ scale: 1.04 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => {
                          setCoffee(c.id);
                          if (c.id !== 'other') {
                            setStep(2);
                          } else {
                            setCustomCoffeeName('');
                          }
                        }}
                      >
                        <div className={`coffee-icon-circle gradient-${c.id}`}>
                          <IconComponent size={22} />
                        </div>
                        <div className="coffee-type-title">{c.label}</div>
                      </motion.button>
                    );
                  })}
                </div>
                {coffee === 'other' && (
                  <div className="custom-coffee-input-container">
                    <label className="custom-coffee-label">Enter coffee type</label>
                    <input
                      type="text"
                      value={customCoffeeName}
                      onChange={(e) => setCustomCoffeeName(e.target.value)}
                      placeholder="e.g., Cortado, Affogato, Ristretto..."
                      className="custom-coffee-input"
                      autoFocus
                      autoComplete="off"
                    />
                  </div>
                )}
              </div>
            )}

            {step === 2 && (
              <div className="coffee-price-quality">
                <div className="price-input-row">
                  <label className="row-label"><Coffee size={16} /> Price</label>
                  <div className="price-input-wrap">
                    <span className="currency">$</span>
                    <input
                      type="number"
                      inputMode="decimal"
                      step="0.1"
                      min="0"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div className="price-quick-grid">
                  {[5, 5.5, 6, 6.5, 7].map((p) => (
                    <button
                      key={p}
                      className={`price-chip ${Number(price) === p ? 'selected' : ''}`}
                      onClick={() => setPrice(String(p))}
                    >
                      ${p.toFixed(2)}
                    </button>
                  ))}
                </div>

                <div className="quality-row">
                  <label className="row-label"><Star size={16} /> Quality</label>
                  <div className="stars">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <button
                        key={i}
                        className={`star ${i <= quality ? 'filled' : ''}`}
                        onClick={() => setQuality(i)}
                        aria-label={`${i} star${i > 1 ? 's' : ''}`}
                      >
                        <Star size={30} />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="crowd-options">
                {CROWD_LEVELS.map((lvl) => (
                  <motion.button
                    key={lvl.id}
                    className={`crowd-option modern ${crowd === lvl.id ? 'selected' : ''}`}
                    whileHover={{ scale: 1.04 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setCrowd(lvl.id)}
                  >
                    <div className={`crowd-icon-circle level-${lvl.id.replace(' ', '-')}`}>
                      <Users size={22} />
                    </div>
                    <div className="crowd-option-title compact">{lvl.label}</div>
                  </motion.button>
                ))}
              </div>
            )}
          </div>

          {/* Fixed Footer */}
          <div className="coffee-actions">
            <div className="left-actions">
              {step > 1 && (
                <button
                  className="action-button secondary"
                  onClick={() => setStep((s) => ((s - 1) as 1 | 2))}
                >
                  <ArrowLeft size={18} />
                  Back
                </button>
              )}
            </div>
            <div className="right-actions">
              {step === 1 && coffee === 'other' ? (
                <button
                  className={`action-button primary ${canNext ? 'active' : ''}`}
                  disabled={!canNext}
                  onClick={() => setStep(2)}
                >
                  Next
                  <ArrowRight size={18} />
                </button>
              ) : step > 1 && step < 3 ? (
                <button
                  className={`action-button primary ${canNext ? 'active' : ''}`}
                  disabled={!canNext}
                  onClick={() => setStep((s) => ((s + 1) as 2 | 3))}
                >
                  Next
                  <ArrowRight size={18} />
                </button>
              ) : (
                step === 3 ? (
                  <button 
                    className={`action-button primary ${canNext ? 'active' : ''} ${isFilling ? 'filling' : ''}`}
                    disabled={!canNext}
                    onClick={handleSubmit}
                  >
                    <span className="button-content">Submit</span>
                    {isFilling && (
                      <div 
                        className="fill-progress"
                        style={{ width: `${fillProgress}%` }}
                      />
                    )}
                  </button>
                ) : null
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CoffeePriceModal;


